"use client";
import { ArrowDownIcon } from "@/app/assets/icons";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";

type DataItem = {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
};

// Function to split an array into equal parts
const splitIntoColumns = <T,>(arr: T[], columns: number): T[][] => {
  const columnLength = Math.ceil(arr.length / columns);
  return Array.from({ length: columns }, (_, i) =>
    arr.slice(i * columnLength, (i + 1) * columnLength)
  );
};

export const DropdownMenu = ({
  data,
  title,
  urlItem,
  onNavigate,
}: {
  data: DataItem[];
  title?: string;
  urlItem?: string;
  onNavigate?: () => void;
}) => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [toggleMenu, setToggleMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width:1024px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setToggleMenu(false);
      }
    };

    const passiveOptions: AddEventListenerOptions = { passive: true };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, passiveOptions);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener(
        "touchstart",
        handleClickOutside,
        passiveOptions
      );
    };
  }, []);

  const handleToggleMenu = () => {
    setToggleMenu((prev) => !prev);
  };

  const columns = splitIntoColumns(data, 3);
  return isMobile ? (
    <>
      <div className="flex items-center mb-[16px]">
        <Link
          href={urlItem ?? ""}
          prefetch={false}
          onClick={() => {
            onNavigate?.();
            setToggleMenu(false);
          }}
          className="uppercase font-bold text-[16px] leading-[16px] tracking-wider underline-offset-[4px] hover:underline"
        >
          {title}
        </Link>
        <button
          className={`w-[24px] h-[24px] flex items-center justify-center ${
            toggleMenu ? "rotate-180" : ""
          } `}
          onClick={handleToggleMenu}
        >
          <ArrowDownIcon />
        </button>
      </div>
      {toggleMenu && (
        <div className="w-full flex flex-col items-center">
          {data?.map((item) => (
            <Link
              key={item.id}
              href={item.url}
              prefetch={false}
              onClick={() => {
                onNavigate?.();
                setToggleMenu(false);
              }}
              className={`font-bold text-black text-[16px] leading-[16px] uppercase mb-[8px] last:mb-[20px]`}
            >
              {item.text}
            </Link>
          ))}
        </div>
      )}
    </>
  ) : (
    <>
      <button
        className="w-[24px] h-[24px] flex items-center justify-center"
        onClick={handleToggleMenu}
      >
        <ArrowDownIcon />
      </button>
      {toggleMenu && (
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-1 bg-white p-1 rounded-[8px] max-w-[466px] w-full absolute top-[40px] border border-[#ccc]"
          ref={menuRef}
        >
          {columns.map((column, index) => (
            <div key={index} className="flex flex-col space-y-[8px]">
              {column.map((item) => (
                <Link
                  key={item.id}
                  href={item.url}
                  prefetch={false}
                  onClick={handleToggleMenu}
                  className={`font-bold text-black uppercase text-[16px] leading-[16px] hover:underline`}
                >
                  {item.text}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
};
